import { Plan } from './types'

export const DEFAULT_PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    entitlements: [
      'api.read',
      'user.profile',
      'org.basic'
    ],
    seats: 1,
    metadata: {
      description: 'Free tier with basic features',
      price: 0,
      currency: 'USD'
    }
  },
  {
    id: 'basic',
    name: 'Basic',
    entitlements: [
      'api.read',
      'api.write',
      'user.profile',
      'org.basic',
      'org.members',
      'billing.basic'
    ],
    seats: 3,
    metadata: {
      description: 'Basic plan with team features',
      price: 9,
      currency: 'USD',
      billing: 'monthly'
    }
  },
  {
    id: 'pro',
    name: 'Pro',
    entitlements: [
      'api.read',
      'api.write',
      'api.admin',
      'user.profile',
      'org.basic',
      'org.members',
      'org.admin',
      'billing.basic',
      'billing.advanced',
      'analytics.basic'
    ],
    seats: 10,
    metadata: {
      description: 'Pro plan with advanced features',
      price: 29,
      currency: 'USD',
      billing: 'monthly'
    }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    entitlements: [
      'api.read',
      'api.write',
      'api.admin',
      'user.profile',
      'org.basic',
      'org.members',
      'org.admin',
      'billing.basic',
      'billing.advanced',
      'analytics.basic',
      'analytics.advanced',
      'support.priority',
      'sso.saml',
      'audit.logs'
    ],
    seats: 100,
    metadata: {
      description: 'Enterprise plan with all features',
      price: 99,
      currency: 'USD',
      billing: 'monthly',
      custom: true
    }
  }
]

// Common entitlement groups
export const ENTITLEMENT_GROUPS = {
  API: {
    READ: 'api.read',
    WRITE: 'api.write',
    ADMIN: 'api.admin'
  },
  USER: {
    PROFILE: 'user.profile',
    SETTINGS: 'user.settings'
  },
  ORGANIZATION: {
    BASIC: 'org.basic',
    MEMBERS: 'org.members',
    ADMIN: 'org.admin',
    BILLING: 'org.billing'
  },
  BILLING: {
    BASIC: 'billing.basic',
    ADVANCED: 'billing.advanced',
    INVOICES: 'billing.invoices'
  },
  ANALYTICS: {
    BASIC: 'analytics.basic',
    ADVANCED: 'analytics.advanced',
    EXPORT: 'analytics.export'
  },
  SUPPORT: {
    BASIC: 'support.basic',
    PRIORITY: 'support.priority',
    DEDICATED: 'support.dedicated'
  },
  SSO: {
    SAML: 'sso.saml',
    OIDC: 'sso.oidc'
  },
  AUDIT: {
    LOGS: 'audit.logs',
    EXPORT: 'audit.export'
  }
} as const

// Plan comparison utilities
export function getPlanById(id: string): Plan | null {
  return DEFAULT_PLANS.find(plan => plan.id === id) || null
}

export function getPlanEntitlements(planId: string): string[] {
  const plan = getPlanById(planId)
  return plan?.entitlements || []
}

export function comparePlans(plan1: string, plan2: string): number {
  const planOrder = ['free', 'basic', 'pro', 'enterprise']
  const index1 = planOrder.indexOf(plan1)
  const index2 = planOrder.indexOf(plan2)
  
  if (index1 === -1 || index2 === -1) {
    return 0
  }
  
  return index1 - index2
}

export function isPlanUpgrade(fromPlan: string, toPlan: string): boolean {
  return comparePlans(fromPlan, toPlan) < 0
}

export function isPlanDowngrade(fromPlan: string, toPlan: string): boolean {
  return comparePlans(fromPlan, toPlan) > 0
}

export function getUpgradeOptions(currentPlan: string): Plan[] {
  const currentIndex = DEFAULT_PLANS.findIndex(plan => plan.id === currentPlan)
  if (currentIndex === -1) {
    return DEFAULT_PLANS
  }
  
  return DEFAULT_PLANS.slice(currentIndex + 1)
}

export function getDowngradeOptions(currentPlan: string): Plan[] {
  const currentIndex = DEFAULT_PLANS.findIndex(plan => plan.id === currentPlan)
  if (currentIndex === -1) {
    return []
  }
  
  return DEFAULT_PLANS.slice(0, currentIndex)
}

export function getPlanSeats(planId: string): number {
  const plan = getPlanById(planId)
  return plan?.seats || 1
}

export function canAddMember(planId: string, currentMembers: number): boolean {
  const maxSeats = getPlanSeats(planId)
  return currentMembers < maxSeats
}

export function getPlanPrice(planId: string): number {
  const plan = getPlanById(planId)
  return plan?.metadata?.price || 0
}

export function getPlanCurrency(planId: string): string {
  const plan = getPlanById(planId)
  return plan?.metadata?.currency || 'USD'
}

export function getPlanBilling(planId: string): string {
  const plan = getPlanById(planId)
  return plan?.metadata?.billing || 'monthly'
}

export function isCustomPlan(planId: string): boolean {
  const plan = getPlanById(planId)
  return plan?.metadata?.custom === true
}

export function getPlanDescription(planId: string): string {
  const plan = getPlanById(planId)
  return plan?.metadata?.description || ''
}

// Entitlement utilities
export function hasEntitlement(entitlements: string[], entitlement: string): boolean {
  return entitlements.includes(entitlement)
}

export function hasAnyEntitlement(entitlements: string[], requiredEntitlements: string[]): boolean {
  return requiredEntitlements.some(entitlement => entitlements.includes(entitlement))
}

export function hasAllEntitlements(entitlements: string[], requiredEntitlements: string[]): boolean {
  return requiredEntitlements.every(entitlement => entitlements.includes(entitlement))
}

export function getEntitlementGroup(entitlement: string): string | null {
  for (const [groupName, groupEntitlements] of Object.entries(ENTITLEMENT_GROUPS)) {
    if (Object.values(groupEntitlements).includes(entitlement as any)) {
      return groupName.toLowerCase()
    }
  }
  return null
}

export function getEntitlementsByGroup(entitlements: string[], group: string): string[] {
  const groupKey = group.toUpperCase() as keyof typeof ENTITLEMENT_GROUPS
  if (!(groupKey in ENTITLEMENT_GROUPS)) {
    return []
  }
  const groupEntitlements = ENTITLEMENT_GROUPS[groupKey]
  if (!groupEntitlements) {
    return []
  }
  const groupValues = Object.values(groupEntitlements) as string[]
  return entitlements.filter(entitlement => groupValues.includes(entitlement))
}
